/**
 * Owner-only seat management panel — shown inside /crm-app for business account owners.
 * Lets them invite teammates, view pending invitations, remove members, and upgrade seats.
 */
import { useEffect, useState } from "react";
import { useCRMAuth } from "@/contexts/CRMAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API } from "@/App";
import { Users, UserPlus, Trash2, Mail, Crown, Clock, X, CreditCard } from "lucide-react";

const SEAT_PRICE = 9.99;

export const SeatManagementPanel = () => {
  const { crmUser, organization, getToken, refreshOrg } = useCRMAuth();
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const isOwner = crmUser?.role === "owner";
  const isBusiness = crmUser?.account_type === "business";

  const fetchOrg = async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/crm/auth/organization`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
        setPending(data.pending_invitations || []);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner && isBusiness) fetchOrg();
    // eslint-disable-next-line
  }, [isOwner, isBusiness]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await fetch(`${API}/crm/auth/seats/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ email: inviteEmail, name: inviteName }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to invite");
      }
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteName("");
      await fetchOrg();
      await refreshOrg();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (invitation_id) => {
    if (!window.confirm("Revoke this invitation?")) return;
    try {
      const res = await fetch(`${API}/crm/auth/seats/invitations/${invitation_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      toast.success("Invitation revoked");
      await fetchOrg();
    } catch {
      toast.error("Failed to revoke");
    }
  };

  const handleRemoveMember = async (member_id, name) => {
    if (!window.confirm(`Remove ${name} from your CRM workspace?`)) return;
    try {
      const res = await fetch(`${API}/crm/auth/seats/members/${member_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success("Member removed, seat freed");
      await fetchOrg();
      await refreshOrg();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleStartCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${API}/crm/org/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          seats: organization?.seats_total || 1,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Checkout failed");
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message);
      setCheckoutLoading(false);
    }
  };

  if (!isOwner || !isBusiness) return null;

  const seatsTotal = organization?.seats_total || 1;
  const seatsUsed = (members?.length || 0) + (pending?.length || 0);
  const isPaid = organization?.subscription_status === "active";

  return (
    <section className="py-8 bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-600" />
              Team & Seats
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {seatsUsed} of {seatsTotal} seats used
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={isPaid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
              {isPaid ? "Active subscription" : "Trial"}
            </Badge>
            {!isPaid && (
              <Button
                onClick={handleStartCheckout}
                disabled={checkoutLoading}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="upgrade-checkout-btn"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {checkoutLoading ? "Loading..." : `Subscribe • £${(seatsTotal * SEAT_PRICE).toFixed(2)}/mo`}
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Invite */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-indigo-600" />
              Invite a teammate
            </h3>
            <form onSubmit={handleInvite} className="space-y-3" data-testid="invite-form">
              <div>
                <Label htmlFor="inv-email">Work email</Label>
                <Input
                  id="inv-email"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="alice@company.com"
                  data-testid="invite-email"
                />
              </div>
              <div>
                <Label htmlFor="inv-name">Name (optional)</Label>
                <Input
                  id="inv-name"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="Alice Worker"
                  data-testid="invite-name"
                />
              </div>
              <Button
                type="submit"
                disabled={inviting || seatsUsed >= seatsTotal}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="invite-submit"
              >
                <Mail className="h-4 w-4 mr-2" />
                {inviting ? "Sending..." : "Send Invitation"}
              </Button>
              {seatsUsed >= seatsTotal && (
                <p className="text-xs text-amber-600 text-center">
                  All seats are full. Upgrade to add more.
                </p>
              )}
            </form>
          </div>

          {/* Members + Pending */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-3">Members & invitations</h3>
            {loading ? (
              <p className="text-sm text-slate-500">Loading...</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {members.map((m) => (
                  <div
                    key={m.crm_user_id}
                    className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2"
                    data-testid={`member-${m.crm_user_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
                      <p className="text-xs text-slate-500 truncate">{m.email}</p>
                    </div>
                    {m.role === "owner" ? (
                      <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">
                        <Crown className="h-3 w-3 mr-1" /> Owner
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleRemoveMember(m.crm_user_id, m.name)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
                {pending.map((p) => (
                  <div
                    key={p.invitation_id}
                    className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                    data-testid={`pending-${p.invitation_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{p.email}</p>
                      <p className="text-xs text-amber-700 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Invitation pending
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleRevoke(p.invitation_id)}
                      data-testid={`revoke-${p.invitation_id}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {members.length === 0 && pending.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No members yet</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
